<!-- src/components/BargainComponent.vue -->
<template>
  <div class="bargain-backdrop" :class="{ submitted: submitted }">
    <div class="bargain-panel">
      <span class="bargain-corner bargain-corner--tl" aria-hidden="true"></span>
      <span class="bargain-corner bargain-corner--tr" aria-hidden="true"></span>
      <span class="bargain-corner bargain-corner--bl" aria-hidden="true"></span>
      <span class="bargain-corner bargain-corner--br" aria-hidden="true"></span>

      <CodexHeading :label="$t('playerComponents.bargain.title')" tone="gold" spine="head"/>

      <div class="bargain-columns">
        <!-- Left Column: Your Inventory -->
        <div class="column inventory-column">
          <h3 class="column-label">{{ $t('playerComponents.bargain.yourInventory') }}</h3>
          <!-- Clicking an item immediately offers it, unless already submitted -->
          <ItemHolder :items="itemsAvailableToSubmit" @item-clicked="handleItemClicked"
                      :disabled="submitted" :show-header="false" :overlay="false"
                      :row-count="4"
                      :col-count="3"
          />
          <p class="column-hint">
            {{ itemsAvailableToSubmit.length
              ? $t('playerComponents.bargain.hintOfferItem')
              : $t('playerComponents.bargain.hintNothingLeft') }}
          </p>
        </div>

        <!-- Center Column: Your Offer -->
        <div class="column offer-column">
          <h3 class="column-label">{{ $t('playerComponents.bargain.youOffer') }}</h3>
          <ItemHolder :items="yourOffer" @item-clicked="removeOfferItem"
                      :disabled="submitted" :show-header="false" :overlay="false"
                      :row-count="4"
                      :col-count="3"
          />
          <p class="column-hint">
            {{ yourOffer.length
              ? $t('playerComponents.bargain.hintTakeBack')
              : $t('playerComponents.bargain.hintNothingOffered') }}
          </p>
        </div>

        <!-- Right Column: Target's Offer -->
        <div class="column target-offer-column">
          <h3 class="column-label">{{ $t('playerComponents.bargain.theyOffer') }}</h3>
          <ItemHolder :items="targetOffer" :show-header="false" :overlay="false" :disabled="true"
                      :row-count="4"
                      :col-count="3"
          />
          <p class="column-hint">
            {{ targetOffer.length
              ? $t('playerComponents.bargain.hintWaitingOnYou')
              : $t('playerComponents.bargain.hintNothingOffered') }}
          </p>
        </div>
      </div>

      <div class="action">
        <!-- Sealing is only possible once something is on the table -->
        <HudButton
            v-if="yourOffer.length && !submitted"
            :type="ButtonType.SUBMIT"
            @click="submitOffers"
        >
          {{ $t('playerComponents.bargain.seal') }}
        </HudButton>
        <!-- Calling the whole bargain off -->
        <HudButton
            :type="ButtonType.CANCEL"
            @click="rejectOffers"
        >
          {{ $t('playerComponents.bargain.walkAway') }}
        </HudButton>
      </div>
    </div>
  </div>
</template>

<script>
import ItemHolder from "@/components/Item/ItemHolder.vue";
import ItemCell from "@/components/Item/ItemCell.vue";
import HudButton from "@/components/btn/HudButton.vue";
import CodexHeading from "@/components/CodexRail/CodexHeading.vue";
import {ButtonType} from "@/types/ButtonType";
import bargainService from "@/services/bargainService.js";

export default {
  name: "BargainComponent",
  components: {
    ItemHolder,
    ItemCell,
    HudButton,
    CodexHeading,
  },
  props: {
    // Your inventory is passed as a prop.
    inventory: {
      type: Array,
      default: () => [],
    },
    // Optionally, an active bargain ID may be passed in.
    bargainId: {
      type: String,
      default: null,
    },
    // The acting character, used to work out which side of the bargain we are on.
    characterId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      ButtonType,
      // The active bargain ID.
      currentBargainId: this.bargainId,
      // Items this character has put on the table.
      yourOffer: [],
      // Items the other side has put on the table.
      targetOffer: [],
      // Once submitted, no further modifications are allowed.
      submitted: false,
    };
  },
  mounted() {
    // Load the bargain details if a bargain is active.
    if (this.currentBargainId) {
      this.loadBargainDetails();
    }
  },
  watch: {
    bargainId(newId) {
      this.currentBargainId = newId;
      this.yourOffer = [];
      this.targetOffer = [];
      this.submitted = false;
      if (newId) this.loadBargainDetails();
    },
  },
  computed: {
    // return filtered inventory items that are not yet offered
    itemsAvailableToSubmit() {
      const offered = new Set(this.yourOffer.map(offer => offer.id));
      return (this.inventory || []).filter(item => !offered.has(item.id));
    },
  },
  methods: {
    /**
     * Automatically offer an inventory item when clicked.
     * If the bargain is not submitted, the item is immediately added via the API.
     *
     * @param {Object} item - The clicked inventory item.
     */
    async handleItemClicked(item) {
      if (this.submitted || !this.currentBargainId) return;
      try {
        // Add the item to the bargain using its unique ID.
        console.debug("Offering item:", {item});
        await bargainService.addItemToBargain(this.currentBargainId, this.worldItemId(item));
        // Refresh bargain details to update both sides’ offered items.
        await this.loadBargainDetails();
      } catch (error) {
        console.error("Error offering item:", error);
      }
    },

    /**
     * Remove an offered item.
     * Allowed only if the bargain has not been submitted.
     *
     * @param {Object} item - The offered item to remove.
     */
    async removeOfferItem(item) {
      if (this.submitted || !this.currentBargainId) return;
      try {
        console.debug("Removing offered item:", {item});
        await bargainService.removeItemFromBargain(this.currentBargainId, this.worldItemId(item));
        await this.loadBargainDetails();
      } catch (error) {
        console.error("Error removing offered item:", error);
      }
    },

    /**
     * Load the bargain details from the API.
     * Expects a response in the following format:
     * {
     *   id: string,
     *   side_a_offered_items: Array,
     *   side_b_offered_items: Array,
     *   side_a_accepted: boolean,
     *   side_b_accepted: boolean,
     *   cancelled: boolean,
     *   completed: boolean,
     *   side_a: string,
     *   side_b: string
     * }
     */
    async loadBargainDetails() {
      if (!this.currentBargainId) return;
      try {
        const data = await bargainService.getBargainDetails(this.currentBargainId);
        console.debug("Loaded bargain details:", {data});
        // The offered items nest the world item under `item` (serializer depth=2).
        const sideAItems = (data.side_a_offered_items || []).map(i => i.item).filter(Boolean);
        const sideBItems = (data.side_b_offered_items || []).map(i => i.item).filter(Boolean);
        // Work out which side we are on rather than assuming side A.
        const onSideB = this.characterId != null && this.sideId(data.side_b) === this.characterId;
        this.yourOffer = onSideB ? sideBItems : sideAItems;
        this.targetOffer = onSideB ? sideAItems : sideBItems;
        this.submitted = onSideB ? !!data.side_b_accepted : !!data.side_a_accepted;
      } catch (error) {
        console.error("Error loading bargain details:", error);
      }
    },

    /**
     * A bargain side may be serialized as a bare id or as a nested object.
     */
    sideId(side) {
      return side && typeof side === "object" ? side.id : side;
    },

    /**
     * The bargain items API is keyed on the world item id.
     */
    worldItemId(item) {
      return item && typeof item === "object" ? item.id : item;
    },

    /**
     * Finalize your offers. Once submitted, the UI becomes non-interactive.
     */
    async submitOffers() {
      this.submitted = true;
      console.debug("Offers submitted!");
      await bargainService.acceptBargain(this.currentBargainId);
      this.$emit("bargain-submitted");
      this.$emit("bargain-completed");
    },
    async rejectOffers() {
      this.submitted = true;
      console.debug("Offers rejected!");
      await bargainService.rejectBargain(this.currentBargainId);
      this.$emit("bargain-rejected");
      this.$emit("bargain-completed");
    },
  },
};
</script>

<style scoped>
/* HUD tokens, matched to the in-game action log and tactical readouts. */
.bargain-backdrop {
  --bg-gold: #f2c489;
  --bg-cyan: #3ee9f0;
  --bg-ink: #d3e0e6;
  --bg-dim: #7f929c;
  --bg-rail: rgba(0, 255, 255, 0.18);
  --bg-slab: rgba(11, 18, 24, 0.72);
  --codex-spine-w: 14px;
  --codex-spine-gap: 0.9rem;
  --codex-spine: rgba(96, 166, 184, 0.85);

  position: fixed;
  top: 0;
  left: 0;
  z-index: 9000; /* Modal overlay - above action bar (2000) but below dice roller (10000) */
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  width: 100%;
  height: 100%;
  padding: 1.5rem 1rem;
  font-family: 'IBM Plex Sans', system-ui, sans-serif;
  background: radial-gradient(ellipse at center, rgba(3, 11, 16, 0.72) 0%, rgba(2, 6, 9, 0.92) 100%);
  backdrop-filter: blur(6px) saturate(0.85);
  -webkit-backdrop-filter: blur(6px) saturate(0.85);
}

/* When submitted, gray out the container and disable interactions */
.bargain-backdrop.submitted {
  opacity: 0.5;
  pointer-events: none;
}

/* The same frame the dossier and the inventory use, so the three panels read
   as one system rather than three one-off overlays. */
.bargain-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  margin: auto;
  padding: 1.1rem 1.35rem 1.35rem;
  color: var(--bg-ink);
  background: linear-gradient(160deg, rgba(12, 19, 25, 0.96) 0%, rgba(6, 10, 14, 0.985) 100%);
  border: 1px solid var(--bg-rail);
  border-radius: 0.375rem;
  box-shadow:
      0 24px 64px rgba(0, 0, 0, 0.72),
      inset 0 1px 0 rgba(62, 233, 240, 0.06);
}

.bargain-panel::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background-image:
      linear-gradient(rgba(62, 233, 240, 0.035) 1px, transparent 1px),
      linear-gradient(90deg, rgba(62, 233, 240, 0.035) 1px, transparent 1px);
  background-size: 2.25rem 2.25rem;
  mask-image: radial-gradient(ellipse at 50% 0%, #000 0%, transparent 78%);
  pointer-events: none;
}

.bargain-panel > *:not(.bargain-corner) {
  position: relative;
  z-index: 1;
}

.bargain-corner {
  position: absolute;
  z-index: 1;
  width: 0.85rem;
  height: 0.85rem;
  border: 1px solid rgba(62, 233, 240, 0.45);
  pointer-events: none;
}

.bargain-corner--tl { top: -1px; left: -1px; border-width: 1px 0 0 1px; }
.bargain-corner--tr { top: -1px; right: -1px; border-width: 1px 1px 0 0; }
.bargain-corner--bl { bottom: -1px; left: -1px; border-width: 0 0 1px 1px; }
.bargain-corner--br { bottom: -1px; right: -1px; border-width: 0 1px 1px 0; }

.bargain-columns {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.column {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 0.7rem 0.75rem;
  background: var(--bg-slab);
  border: 1px solid rgba(255, 255, 255, 0.045);
  border-radius: 0.25rem;
}

/* The player's own side of the table is the live one. */
.offer-column {
  border-color: var(--bg-rail);
}

/* Same label idiom as the action log's readout-title, over its hairline rail. */
.column-label {
  margin: 0;
  padding-bottom: 0.35rem;
  width: 100%;
  border-bottom: 1px solid var(--bg-rail);
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.58rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-align: center;
  color: var(--bg-gold);
}

/* The offered column is the one the player is acting on, so it reads as live. */
.offer-column .column-label {
  color: var(--bg-cyan);
  border-bottom-color: rgba(62, 233, 240, 0.45);
}

/* Nothing is clickable on the other side's shelf, so it stays recessed. */
.target-offer-column .column-label {
  color: var(--bg-dim);
}

.column-hint {
  margin: 0;
  min-height: 1rem;
  font-family: 'Orbitron', system-ui, sans-serif;
  font-size: 0.55rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-align: center;
  color: var(--bg-dim);
}

.action {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding-top: 0.25rem;
}

@media (max-width: 60rem) {
  .bargain-columns {
    gap: 0.5rem;
  }
}
</style>
